import React, { useState } from "react";
import { Check, X, RotateCcw, UserCheck, ClipboardCheck, Ban } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { StatusBadge } from "../StatusBadge";

/** Every action here reuses the same 2-hour protected-block conflict check as public
 * scheduling (interview_scheduling_service._reserve_new_slot) - manual scheduling can never
 * bypass it. */
export const BookingActionsModal = ({
  booking, onClose, onConfirm, onCancel, onReschedule, onProposeTime, onAssignInterviewer, onComplete, onNoShow,
}) => {
  const [rescheduleDate, setRescheduleDate] = useState(booking.date || "");
  const [rescheduleTime, setRescheduleTime] = useState(booking.start_time || "10:00");
  const [interviewerName, setInterviewerName] = useState(booking.interviewer_name || "");
  const [cancelReason, setCancelReason] = useState("");

  const isPending = booking.status === "PENDING_CONFIRMATION";
  const isConfirmed = booking.status === "CONFIRMED";
  const isActive = ["HELD", "PENDING_CONFIRMATION", "CONFIRMED", "RESCHEDULE_REQUESTED"].includes(booking.status);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="booking-actions-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Interview Booking <StatusBadge status={booking.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1 text-sm text-gray-600 border-b border-gray-100 pb-3">
          <p><span className="text-gray-500">Date/time:</span> {booking.date || "-"} @ {booking.start_time || "-"} JST</p>
          <p><span className="text-gray-500">Protected block:</span> {booking.protected_block_start || "-"} - {booking.protected_block_end || "-"} JST</p>
          <p><span className="text-gray-500">Applicant:</span> {booking.applicant_name || booking.applicant_id || "-"}</p>
          <p><span className="text-gray-500">Interviewer:</span> {booking.interviewer_name || "Unassigned"}</p>
        </div>

        {isPending && (
          <button data-testid="booking-modal-confirm" onClick={onConfirm} className="flex items-center justify-center gap-2 py-2.5 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600">
            <Check size={15} /> Confirm Interview
          </button>
        )}

        {isConfirmed && (
          <div className="flex gap-2">
            <button data-testid="booking-modal-complete" onClick={onComplete} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full bg-blue-950 text-white text-sm font-semibold hover:bg-blue-900">
              <ClipboardCheck size={15} /> Mark Completed
            </button>
            <button data-testid="booking-modal-no-show" onClick={onNoShow} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50">
              <Ban size={15} /> No-Show
            </button>
          </div>
        )}

        {isActive && (
          <div className="space-y-2 border-t border-gray-100 pt-3">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Reschedule / Propose New Time</p>
            <div className="flex gap-2">
              <input data-testid="booking-modal-reschedule-date" type="date" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm" />
              <input data-testid="booking-modal-reschedule-time" type="time" step={1800} value={rescheduleTime} onChange={(e) => setRescheduleTime(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            </div>
            <div className="flex gap-2">
              <button data-testid="booking-modal-reschedule" onClick={() => onReschedule(rescheduleDate, rescheduleTime)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full border border-blue-950 text-blue-950 text-xs font-semibold hover:bg-blue-50">
                <RotateCcw size={13} /> Reschedule Now
              </button>
              <button data-testid="booking-modal-propose-time" onClick={() => onProposeTime(rescheduleDate, rescheduleTime)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full border border-amber-400 text-amber-600 text-xs font-semibold hover:bg-amber-50">
                Propose Time (Applicant Confirms)
              </button>
            </div>
          </div>
        )}

        <div className="flex gap-2 border-t border-gray-100 pt-3">
          <input data-testid="booking-modal-interviewer-input" placeholder="Interviewer name" value={interviewerName} onChange={(e) => setInterviewerName(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          <button data-testid="booking-modal-assign-interviewer" onClick={() => onAssignInterviewer(interviewerName)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-xs font-semibold hover:bg-gray-50">
            <UserCheck size={13} /> Assign
          </button>
        </div>

        {isActive && (
          <div className="space-y-2 border-t border-gray-100 pt-3">
            <input data-testid="booking-modal-cancel-reason" placeholder="Cancellation reason (optional)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <button data-testid="booking-modal-cancel" onClick={() => onCancel(cancelReason)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50">
              <X size={15} /> Cancel Interview
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
